import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { IDatasource } from 'ag-grid-community';
import { DataGrid, type ColDef, type GridApi } from '../../../../components/ui/table';
import { useAsyncEffect } from '../../../../hooks/useAsyncEffect';
import { fetchAcquirerTerminalsGrid, fetchAcquirerTerminalsList } from '../../api/api';
import { createAcquirerTerminalColumnDefs } from '../../table/columnDefs';
import {
  GRID_PAGE_SIZE,
  GRID_PAGE_SIZE_SELECTOR,
  TERMINALS_GRID_DEFAULT_COL_DEF,
} from '../../table/gridOptions';
import { LIST_CONSOLE_AG_GRID_CLASS } from '../../../../components/shared/ListConsoleLayout';
import type {
  AcquirerTerminalGridRow,
  AcquirerTerminalRow,
  CountryLookup,
  TerminalsColumnFilters,
  TerminalsGridContext,
} from '../../models/types';
import {
  buildCountryById,
  buildCurrencyById,
  mapAgGridFilterModelToTerminalsFilters,
  resolveCurrencyCodesFromCountryIds,
  resolveDisplayName,
} from '../../utils/utils';
import { ACCENT_COLORS, EMPTY_TERMINALS_COLUMN_FILTERS } from '../../constants/constants';

export type AcquirerTerminalsState = {
  rows: AcquirerTerminalRow[];
  statsRows: AcquirerTerminalRow[];
  loading: boolean;
  error: string | null;
};

type AcquirerTerminalsProps = {
  active: boolean;
  debouncedSearch: string;
  columnFilters: TerminalsColumnFilters;
  countryLookups: CountryLookup[];
  countryIdsByCurrency: Record<string, number[]>;
  acquirerOptions: Array<{ id: number; name: string }>;
  gridContext: TerminalsGridContext;
  gridApiRef: React.MutableRefObject<GridApi | null>;
  emptyMessage: string;
  onStateChange: (state: AcquirerTerminalsState) => void;
  refreshToken?: number;
  gridFilterEpoch?: number;
  skipDatasourceResetRef: React.MutableRefObject<boolean>;
  onGridFilterChanged?: () => void;
};

const AcquirerTerminals: React.FC<AcquirerTerminalsProps> = ({
  active,
  debouncedSearch,
  columnFilters,
  countryLookups,
  countryIdsByCurrency,
  acquirerOptions,
  gridContext,
  gridApiRef,
  emptyMessage,
  onStateChange,
  refreshToken = 0,
  gridFilterEpoch = 0,
  skipDatasourceResetRef,
  onGridFilterChanged,
}) => {
  void gridFilterEpoch;
  const [acquirerTerminals, setAcquirerTerminals] = useState<AcquirerTerminalRow[]>([]);
  const [statsAcquirerTerminals, setStatsAcquirerTerminals] = useState<AcquirerTerminalRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [tablePageSize, setTablePageSize] = useState(GRID_PAGE_SIZE);
  const [error, setError] = useState<string | null>(null);

  const countryById = useMemo(() => buildCountryById(countryLookups), [countryLookups]);
  const currencyById = useMemo(() => buildCurrencyById(countryLookups), [countryLookups]);
  const countryNameById = useMemo(
    () =>
      countryLookups.reduce<Record<number, string>>((acc, country) => {
        acc[country.autoId] = country.countryName;
        return acc;
      }, {}),
    [countryLookups],
  );
  const acquirerById = useMemo(
    () =>
      acquirerOptions.reduce<Record<number, string>>((acc, acquirer) => {
        acc[acquirer.id] = acquirer.name;
        return acc;
      }, {}),
    [acquirerOptions],
  );

  const filtersRef = React.useRef({ debouncedSearch, columnFilters, countryIdsByCurrency });
  filtersRef.current = { debouncedSearch, columnFilters, countryIdsByCurrency };
  const lookupsRef = React.useRef({ countryById, countryNameById, acquirerById, currencyById });
  lookupsRef.current = { countryById, countryNameById, acquirerById, currencyById };

  const fetchStats = useCallback(async () => {
    try {
      const { rows } = await fetchAcquirerTerminalsGrid({
        search: '',
        columnFilters: EMPTY_TERMINALS_COLUMN_FILTERS,
      });
      setStatsAcquirerTerminals(rows);
    } catch (e) {
      console.error('Failed to fetch acquirer terminal stats:', e);
    }
  }, []);

  const tableDatasource = useMemo<IDatasource>(
    () => ({
      getRows: async (params) => {
        const pageSize = params.endRow - params.startRow;
        const page = pageSize > 0 ? Math.floor(params.startRow / pageSize) : 0;
        setLoading(true);
        setError(null);
        try {
          const rawFilterModel =
            (params.filterModel as Record<string, unknown> | null | undefined) ??
            (params.api?.getFilterModel() as Record<string, unknown> | null | undefined) ??
            null;
          const nativeColumnFilters = mapAgGridFilterModelToTerminalsFilters(rawFilterModel);
          const mergedColumnFilters: TerminalsColumnFilters = {
            ...filtersRef.current.columnFilters,
            ...nativeColumnFilters,
          };
          const { rows, totalElements } = await fetchAcquirerTerminalsList({
            search: filtersRef.current.debouncedSearch,
            columnFilters: mergedColumnFilters,
            countryIdsByCurrency: filtersRef.current.countryIdsByCurrency,
            page,
            size: pageSize,
          });
          const lookups = lookupsRef.current;
          const gridRows: AcquirerTerminalGridRow[] = rows.map((t, idx) => {
            const country = t.countryId ? lookups.countryById[t.countryId] : undefined;
            const currencies = resolveCurrencyCodesFromCountryIds(t.currencies, lookups.currencyById);
            return {
              terminalId: t.terminalId,
              name: t.name,
              acquirerName: resolveDisplayName(t.acquirerName, lookups.acquirerById, t.acquirerId),
              countryLabel: resolveDisplayName(t.countryName, lookups.countryNameById, t.countryId),
              countryFlag: country?.countryFlag,
              countryId: country?.autoId ?? t.countryId,
              currenciesLabel: currencies.length > 0 ? currencies.join(', ') : '—',
              currencies,
              flowName: t.flowName || '—',
              industryName: t.industryName || '—',
              trafficName: t.trafficName || '—',
              successTransactionVolume: t.successTransactionVolume,
              accent: ACCENT_COLORS[idx % ACCENT_COLORS.length],
              raw: t,
              createdAt: t.createdAt,
              updatedAt: t.updatedAt,
            };
          });
          setAcquirerTerminals(rows);
          params.successCallback(gridRows, totalElements);
        } catch (e) {
          console.error(e);
          setAcquirerTerminals([]);
          setError('Could not load acquirer terminals. Please try again.');
          params.failCallback();
        } finally {
          setLoading(false);
        }
      },
    }),
    [],
  );

  useEffect(() => {
    if (!active) return;
    if (skipDatasourceResetRef.current) {
      skipDatasourceResetRef.current = false;
      return;
    }
    gridApiRef.current?.setGridOption('datasource', tableDatasource);
  }, [
    active,
    debouncedSearch,
    columnFilters,
    countryIdsByCurrency,
    countryById,
    countryNameById,
    acquirerById,
    currencyById,
    refreshToken,
    tableDatasource,
    gridApiRef,
    skipDatasourceResetRef,
  ]);

  useAsyncEffect(async (isActive) => {
    await fetchStats();
    if (!isActive()) return;
  }, [fetchStats, refreshToken]);

  useEffect(() => {
    onStateChange({
      rows: acquirerTerminals,
      statsRows: statsAcquirerTerminals,
      loading,
      error,
    });
  }, [acquirerTerminals, statsAcquirerTerminals, loading, error, onStateChange]);

  const columnDefs = useMemo<ColDef<AcquirerTerminalGridRow>[]>(
    () => createAcquirerTerminalColumnDefs(),
    [],
  );

  const handleTablePaginationChanged = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;
    const newSize = api.paginationGetPageSize();
    if (newSize && newSize !== tablePageSize) {
      setTablePageSize(newSize);
    }
  }, [gridApiRef, tablePageSize]);

  if (!active) return null;

  return (
    <DataGrid<AcquirerTerminalGridRow>
      rowModelType="infinite"
      datasource={tableDatasource}
      cacheBlockSize={tablePageSize}
      columnDefs={columnDefs}
      context={gridContext}
      loading={loading}
      pagination
      paginationPageSize={tablePageSize}
      paginationPageSizeSelector={GRID_PAGE_SIZE_SELECTOR}
      height="100%"
      className={LIST_CONSOLE_AG_GRID_CLASS}
      onGridReady={(event) => {
        gridApiRef.current = event.api;
      }}
      defaultColDef={TERMINALS_GRID_DEFAULT_COL_DEF}
      gridOptions={{
        suppressRowClickSelection: true,
        getRowId: (params) => String(params.data?.terminalId ?? ''),
        overlayNoRowsTemplate: `<span class="text-sm text-muted-foreground">${emptyMessage}</span>`,
        onFilterChanged: onGridFilterChanged,
        onPaginationChanged: handleTablePaginationChanged,
      }}
    />
  );
};

export default AcquirerTerminals;
